import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatIDR, formatDate, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput, PercentInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DateRangeFilter, firstOfMonth, todayStr, buildDateQuery } from "@/components/DateRangeFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, FilePdf, Receipt, DownloadSimple, UploadSimple, EnvelopeSimple, WhatsappLogo, ArrowRight, PencilSimple, Lock, Eye, MagnifyingGlass, Repeat, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";
import TaxRegsPanel, { calcTotals } from "@/components/TaxRegsPanel";
import ImportPreviewDialog from "@/components/ImportPreviewDialog";
import { SendDocumentDialog } from "@/components/SendDocumentDialog";
import ImportProgressDialog from "@/components/ImportProgressDialog";
import { PartnerCombobox } from "@/components/PartnerCombobox";
import { ProjectCombobox } from "@/components/ProjectCombobox";
import { MultiReceiptDialog, pph23Remaining, pph23OnInvoice } from "@/components/MultiReceiptDialog";

const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = () => ({ product_id: "", product_name: "", item_type: "product", description: "", quantity: 1, unit: "pcs", price: 0, cost_price: 0, discount: 0, discount_percent: 0, tax_rate: 0.11, pph23_rate: 0, revenue_account_code: "4000", cogs_account_code: "5000", inventory_account_code: "1300" });

function ItemsEditor({ items, setItems, products, showCost = false }) {
  const colLabel = "text-[11px] uppercase tracking-wider text-slate-500";
  const cls = {
    prod: showCost ? "col-span-2" : "col-span-3",
    qty:  "col-span-1",
    price:"col-span-3",
    hpp:  "col-span-2",
    disc: "col-span-2",
    tax:  "col-span-2",
    del:  "col-span-1",
  };
  return (
    <div>
      <Label>Item</Label>
      <div className="grid grid-cols-12 gap-2 mt-2" data-testid="items-editor-header">
        <span className={`${cls.prod} ${colLabel}`}>Produk / Jasa</span>
        <span className={`${cls.qty} ${colLabel}`}>Qty</span>
        <span className={`${cls.price} ${colLabel}`}>Harga Satuan (Rp)</span>
        {showCost && <span className={`${cls.hpp} ${colLabel}`}>HPP (Rp)</span>}
        <span className={`${cls.disc} ${colLabel}`}>Diskon % / Rp</span>
        <span className={`${cls.tax} ${colLabel}`}>PPN %</span>
        <span className={cls.del}></span>
      </div>
      {items.map((it, idx) => (
        <React.Fragment key={idx}>
        <div className="grid grid-cols-12 gap-2 mt-2">
          <Select value={it.product_id} onValueChange={(v) => {
            const p = products.find(x => x.id === v);
            const items2 = [...items];
            items2[idx] = { ...items2[idx], product_id: v, product_name: p?.name || "", item_type: p?.item_type || "product", description: p?.description || "", price: p?.sale_price || 0, cost_price: p?.cost_price || 0, unit: p?.base_unit || "pcs", revenue_account_code: p?.revenue_account_code || "4000", cogs_account_code: p?.cogs_account_code || "5000", inventory_account_code: p?.inventory_account_code || "1300" };
            setItems(items2);
          }}>
            <SelectTrigger className={cls.prod} data-testid={`sales-item-product-${idx}`}><SelectValue placeholder="Pilih produk/jasa" /></SelectTrigger>
            <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.item_type === "service" ? "(Jasa)" : `(${p.stock || 0} ${p.base_unit})`}</SelectItem>)}</SelectContent>
          </Select>
          <Input className={cls.qty} type="number" placeholder="Qty" value={it.quantity} onChange={(e) => { const a = [...items]; a[idx].quantity = e.target.value; setItems(a); }} />
          <MoneyInput className={cls.price} placeholder="Harga" value={it.price} onChange={(e) => { const a = [...items]; a[idx].price = e.target.value; setItems(a); }} data-testid={`sales-item-price-${idx}`} />
          {showCost && <MoneyInput className={cls.hpp} placeholder="HPP" value={it.cost_price} onChange={(e) => { const a = [...items]; a[idx].cost_price = e.target.value; setItems(a); }} data-testid={`sales-item-hpp-${idx}`} />}
          <div className={`${cls.disc} flex gap-1 items-stretch`}>
            <button type="button" onClick={() => { const a = [...items]; a[idx] = { ...a[idx], _disc_mode: (a[idx]._disc_mode === "rp" ? "pct" : "rp") }; setItems(a); }} data-testid={`sales-item-discount-mode-${idx}`} className="shrink-0 w-9 text-xs font-mono-plex rounded border border-slate-300 bg-slate-50 hover:bg-slate-100" title="Ganti mode diskon (persen / rupiah)">{(it._disc_mode || "pct") === "rp" ? "Rp" : "%"}</button>
            {(it._disc_mode || "pct") === "rp" ? (
              <MoneyInput className="flex-1 min-w-0" placeholder="Rp" value={it.discount || 0} onChange={(e) => { const a = [...items]; const rp = Number(e.target.value) || 0; const base = (Number(a[idx].quantity) || 0) * (Number(a[idx].price) || 0); a[idx] = { ...a[idx], discount: e.target.value, discount_percent: base > 0 ? +(rp / base * 100).toFixed(2) : 0 }; setItems(a); }} data-testid={`sales-item-discount-${idx}`} />
            ) : (
              <Input className="flex-1 min-w-0" type="number" min="0" max="100" step="0.01" placeholder="%" value={it.discount_percent || 0} onChange={(e) => { const a = [...items]; const pct = Number(e.target.value) || 0; const rp = Math.round((Number(a[idx].quantity) || 0) * (Number(a[idx].price) || 0) * pct / 100); a[idx] = { ...a[idx], discount_percent: e.target.value, discount: rp }; setItems(a); }} data-testid={`sales-item-discount-pct-${idx}`} />
            )}
          </div>
          <PercentInput className={cls.tax} placeholder="PPN" value={it.tax_rate} onChange={(e) => { const a = [...items]; a[idx].tax_rate = e.target.value; setItems(a); }} data-testid={`sales-item-tax-${idx}`} />
          <button className={`${cls.del} text-slate-400 hover:text-rose-600`} onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash size={14} /></button>
        </div>
        <div className="grid grid-cols-12 gap-2 mt-1 items-start">
          <Textarea className="col-span-11 text-xs min-h-[52px]" rows={2} placeholder="Deskripsi item (opsional) — muncul di baris item PDF/faktur. Bisa multi-baris (Enter) dan boleh beda dari data master."
            value={it.description || ""} onChange={(e) => { const a = [...items]; a[idx].description = e.target.value; setItems(a); }}
            data-testid={`sales-item-description-${idx}`} />
          {it.product_id ? (
            <button type="button" className="col-span-1 mt-1 text-slate-400 hover:text-amber-600" title="Reset ke deskripsi master"
              onClick={() => { const p = products.find(x => x.id === it.product_id); const a = [...items]; a[idx].description = p?.description || ""; setItems(a); }}
              data-testid={`sales-item-desc-reset-${idx}`}>
              <ArrowCounterClockwise size={14} />
            </button>
          ) : <span className="col-span-1" />}
        </div>
        {(() => {
          const p = products.find(x => x.id === it.product_id);
          const aliases = p?.aliases || [];
          if (!it.product_id || aliases.length === 0) return null;
          return (
            <div className="grid grid-cols-12 gap-2 items-center pl-1 -mt-1">
              <span className="col-span-3 text-[10px] text-slate-500">Nama tampil di faktur:</span>
              <input list={`aliases-${idx}`} className="col-span-8 h-7 rounded border border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-800"
                value={it.product_name || ""} onChange={(e) => { const a = [...items]; a[idx].product_name = e.target.value; setItems(a); }}
                data-testid={`sales-item-alias-${idx}`} placeholder={p.name} />
              <datalist id={`aliases-${idx}`}>
                <option value={p.name} />
                {aliases.map((al, i) => <option key={i} value={al} />)}
              </datalist>
            </div>
          );
        })()}
        </React.Fragment>
      ))}
      <Button variant="outline" size="sm" className="mt-2" onClick={() => setItems([...items, emptyItem()])}>+ Item</Button>
    </div>
  );
}

export default function Sales() {
  const { workspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [prefillPartner, setPrefillPartner] = useState("");
  const [tab, setTab] = useState("invoices");
  const [search, setSearch] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProg, setImportProg] = useState(null); // { loading, fileName, result }
  const [partners, setPartners] = useState([]);
  const [taxRegs, setTaxRegs] = useState([]);
  const [taxSettings, setTaxSettings] = useState({ use_dpp_nilai_lain: false, default_vat_rate: 12 });
  const [taxSettingsReady, setTaxSettingsReady] = useState(false);
  const [products, setProducts] = useState([]);
  const [cashBanks, setCashBanks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [dpList, setDpList] = useState([]);
  const [dpDlg, setDpDlg] = useState(null); // { order }
  const [dpForm, setDpForm] = useState({ amount: 0, cash_bank_id: "", date: "", notes: "" });
  const [gmailDlg, setGmailDlg] = useState(null); // invoice id
  const [gmailAtts, setGmailAtts] = useState([]);
  const [gmailForm, setGmailForm] = useState({ to: "", cc: "", subject: "", message: "", attachments: ["invoice_pdf"] });
  const [gmailFiles, setGmailFiles] = useState([]); // lampiran tambahan (file kontrak)
  const [gmailTpls, setGmailTpls] = useState([]);
  const [gmailInv, setGmailInv] = useState(null); // simpan invoice utk render placeholder
  const [gmailSending, setGmailSending] = useState(false);
  const [gmailProvider, setGmailProvider] = useState("gmail"); // gmail | smtp
  // Kirim email dokumen non-faktur (Penawaran / Pesanan Penjualan)
  const [docEmailDlg, setDocEmailDlg] = useState(null); // { docType, docId, provider }
  const [emailLogs, setEmailLogs] = useState({}); // docId → log kirim terbaru
  const [openDlg, setOpenDlg] = useState(null); // "quot"|"so"|"do"|"inv"|"ret"|"rcp"
  const [recDlg, setRecDlg] = useState(null); // faktur yang dijadikan template berulang
  const [recForm, setRecForm] = useState({});
  const [recSenders, setRecSenders] = useState([]);
  const [recBusy, setRecBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [multiRcpOpen, setMultiRcpOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null); // { kind, doc } — untuk ganti rekening saat pratinjau faktur
  const [form, setForm] = useState({});

  const [dateRange, setDateRange] = useState({ from: firstOfMonth(), to: todayStr() });
  const load = () => {
    api.get("/partners?type=customer").then((r) => setPartners(r.data));
    api.get("/inventory/products").then((r) => setProducts(r.data));
    api.get("/cashbank/accounts").then((r) => setCashBanks(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/projects?active_only=true").then((r) => setProjects(r.data));
    const dq = buildDateQuery(dateRange);
    api.get(`/sales/quotations${dq}`).then((r) => setQuotations(r.data));
    api.get(`/sales/orders${dq}`).then((r) => setOrders(r.data));
    api.get(`/sales/deliveries${dq}`).then((r) => setDeliveries(r.data));
    api.get(`/sales/invoices${dq}`).then((r) => setInvoices(r.data));
    api.get(`/sales/returns${dq}`).then((r) => setReturns(r.data));
    api.get(`/sales/receipts${dq}`).then((r) => setReceipts(r.data));
    api.get("/sales/dp").then((r) => setDpList(r.data)).catch(() => {});
    ["sales-quotation", "sales-order"].forEach((dt) => api.get("/send-document/logs", { params: { doc_type: dt } }).then((r) => setEmailLogs((m) => ({ ...m, ...(r.data.logs || {}) }))).catch(() => {}));
  };
  useEffect(() => { load(); }, [dateRange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/tax-regulations").then((r) => setTaxRegs(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/efaktur/settings").then((r) => setTaxSettings(r.data)).catch(() => {}).finally(() => setTaxSettingsReady(true)); }, []);

  // AI Chat prefill: FloatingAIChat mengirim location.state { prefillAction, prefill }
  useEffect(() => {
    const st = location.state;
    if (!st?.prefillAction) return;
    const map = { sales_quotation: "quot", sales_order: "so", sales_invoice: "inv" };
    const dlg = map[st.prefillAction];
    if (!dlg) return;
    const d = st.prefill || {};
    const items = (d.items || []).map(i => ({ ...emptyItem(), product_name: i.product_name || i.name || "", item_type: i.item_type || "product", description: i.description || "", quantity: Number(i.quantity) || 1, unit: i.unit || "pcs", price: Number(i.price) || 0, tax_rate: i.tax_rate ?? 0.11 }));
    setEditingId(null);
    setForm({
      date: d.date || today(), due_date: d.due_date || today(),
      partner_id: "", partner_name: d.partner_name || "",
      customer_po_no: "", items: items.length ? items : [emptyItem()],
      notes: d.notes || "Dibuat dari AI Chat", discount_global: 0,
      payment_account_id: "", dpp_nilai_lain: false,
      valid_until: d.valid_until || "", warehouse_id: "", project_id: "", project_name: "",
    });
    setPrefillPartner(d.partner_name || "");
    setOpenDlg(dlg);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state]);

  // Resolve partner_id berdasarkan nama setelah daftar partner & pengaturan pajak termuat
  useEffect(() => {
    if (!prefillPartner || !partners.length || !taxSettingsReady) return;
    const p = partners.find(x => (x.name || "").toLowerCase() === prefillPartner.toLowerCase());
    if (p) setForm(f => ({ ...f, partner_id: p.id, tax_regulation_ids: p.tax_regulation_ids || [], dpp_nilai_lain: !!(taxSettings.use_dpp_nilai_lain && p.is_pkp) }));
    setPrefillPartner("");
  }, [partners, prefillPartner, taxSettingsReady]);
  const filterRows = (arr) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(r => [r.number, r.partner_name, r.invoice_number, r.notes, r.tax_invoice_number, r.status, r.customer_po_no]
      .some(v => (v || "").toString().toLowerCase().includes(q)));
  };
  const pgQuot = usePaginated(filterRows(quotations));
  const pgOrd = usePaginated(filterRows(orders));
  const pgDel = usePaginated(filterRows(deliveries));
  const pgInv = usePaginated(filterRows(invoices));
  const pgRet = usePaginated(filterRows(returns));
  const pgRcp = usePaginated(filterRows(receipts));
  const pgs = { quotations: pgQuot, orders: pgOrd, deliveries: pgDel, invoices: pgInv, returns: pgRet, receipts: pgRcp };
  const rcpInv = openDlg === "rcp" ? invoices.find(i => i.id === form.invoice_id) : null;
  const pph23Rem = rcpInv ? pph23Remaining(rcpInv, receipts) : 0;
  const pph23Inv = rcpInv ? pph23OnInvoice(rcpInv) : 0;
  const autoPph23 = (amt, inv, rem) => {
    const bal = inv?.balance || 0;
    return bal > 0 ? Math.min(rem, Math.round(rem * Number(amt || 0) / bal)) : 0;
  };

  const openDpDlg = (order) => {
    setDpDlg({ order });
    setDpForm({ amount: 0, cash_bank_id: "", date: today(), notes: "", with_ppn: false, tax_rate: 0.11 });
  };
  const submitDp = async () => {
    try {
      await api.post("/sales/dp", { ...dpForm, order_id: dpDlg.order.id, amount: Number(dpForm.amount), tax_rate: (dpForm.tax_rate === "" || dpForm.tax_rate === null || dpForm.tax_rate === undefined) ? 0 : Number(dpForm.tax_rate) });
      toast.success("Uang Muka tercatat & jurnal otomatis dibuat");
      setDpDlg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const assignDpEfaktur = async (dp) => {
    try {
      const r = await api.post(`/efaktur/assign-dp/${dp.id}`);
      toast.success(`e-Faktur DP: ${r.data.tax_invoice_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const downloadDpXml = async (dp) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/efaktur/xml-dp/${dp.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || "Gagal"); }
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `coretax_DP_${dp.number}.xml`; a.click();
    } catch (e) { toast.error(e.message || "Gagal download XML"); }
  };
  const cancelApproval = async (kind, id, num) => {
    if (!window.confirm(`Batalkan approval ${num}? Dokumen kembali ke status Draft supaya bisa diedit.`)) return;
    const notes = window.prompt("Catatan pembatalan (opsional):", "") ?? "";
    try {
      await api.post(`/approvals/${kind}/${id}/cancel-approval`, { notes });
      toast.success("Approval dibatalkan — dokumen kembali ke Draft");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const removeDp = async (dp) => {
    if (!window.confirm(`Hapus Uang Muka ${dp.number}? Jurnal akan dibatalkan.`)) return;
    try { await api.delete(`/sales/dp/${dp.id}`); toast.success("Uang Muka dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openNew = (type) => {
    setEditingId(null);
    setOpenDlg(type);
    const defaults = { date: today(), due_date: today(), partner_id: "", partner_name: "", customer_po_no: "", items: [emptyItem()], notes: "", discount_global: 0, payment_account_id: "", dpp_nilai_lain: false };
    if (type === "do") setForm({ order_id: "", ...defaults });
    else if (type === "ret") setForm({ invoice_id: "", ...defaults, reason: "" });
    else if (type === "rcp") setForm({ invoice_id: "", cash_bank_id: "", date: today(), amount: 0, notes: "" });
    else setForm(defaults);
  };

  const openEdit = (type, doc) => {
    if (doc.is_locked) return toast.error(doc.lock_reason || "Dokumen terkunci");
    if (type === "so" && doc.approval_status === "approved") return toast.error("SO sudah disetujui — tolak dulu untuk edit");
    setEditingId(doc.id);
    const cleanItems = (doc.items || []).map(i => ({ ...i, quantity: Number(i.quantity || 0), price: Number(i.price || 0), tax_rate: Number(i.tax_rate ?? 0.11), pph23_rate: Number(i.pph23_rate ?? 0) }));
    setForm({
      date: doc.date || today(), due_date: doc.due_date || doc.date || today(),
      partner_id: doc.partner_id || "", partner_name: doc.partner_name || "",
      customer_po_no: doc.customer_po_no || "",
      warehouse_id: doc.warehouse_id || "", project_id: doc.project_id || "",
      project_name: doc.project_name || "",
      quotation_id: doc.quotation_id || "",
      order_id: doc.order_id || "",
      delivery_id: doc.delivery_id || "",
      invoice_id: doc.invoice_id || "",
      valid_until: doc.valid_until || "",
      reason: doc.reason || "",
      cash_bank_id: doc.cash_bank_id || "",
      amount: doc.amount || 0,
      items: cleanItems.length ? cleanItems : [emptyItem()],
      notes: doc.notes || "",
      discount_global: doc.discount_global || 0,
      payment_account_id: doc.payment_account_id || "",
      dpp_nilai_lain: doc.dpp_nilai_lain || false,
      tax_regulation_ids: doc.tax_regulation_ids || [],
      dp_ids: doc.dp_ids || [],
    });
    setOpenDlg(type);
  };

  const removeDoc = async (kind, doc) => {
    const label = { quot: "Penawaran", so: "Sales Order", do: "Pengiriman", inv: "Faktur", ret: "Retur", rcp: "Penerimaan" }[kind] || "Dokumen";
    if (!window.confirm(`Hapus ${label} ${doc.number}?${doc.is_multi ? " Ini penerimaan multi faktur — seluruh faktur dalam nomor ini ikut dibatalkan." : ""} Semua efek (jurnal, stok, saldo) akan dibatalkan.`)) return;
    const path = { quot: "/sales/quotations", so: "/sales/orders", do: "/sales/deliveries", inv: "/sales/invoices", ret: "/sales/returns", rcp: "/sales/receipts" }[kind];
    try { await api.delete(`${path}/${doc.id}`); toast.success(`${label} dihapus`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openRecurring = async (inv) => {
    const dom = Number((inv.date || today()).slice(8, 10)) || 1;
    let dueDays = 30;
    if (inv.date && inv.due_date) {
      const diff = Math.round((new Date(inv.due_date) - new Date(inv.date)) / 86400000);
      if (diff >= 0) dueDays = diff;
    }
    setRecForm({ name: `${inv.partner_name} — ${inv.number}`, day_of_month: dom, due_days: dueDays, auto_send: false, sender_user_id: "", active: true });
    setRecDlg(inv);
    try { const s = await api.get("/reminders/senders").catch(() => ({ data: [] })); setRecSenders(s.data || []); } catch { setRecSenders([]); }
  };
  const saveRecurring = async () => {
    const inv = recDlg;
    if (!recForm.name) return toast.error("Nama template wajib diisi");
    setRecBusy(true);
    try {
      const items = (inv.items || []).map(i => ({ ...i, quantity: Number(i.quantity || 0), price: Number(i.price || 0), discount: Number(i.discount || 0), discount_percent: Number(i.discount_percent || 0), tax_rate: Number(i.tax_rate ?? 0.11) }));
      await api.post("/recurring-invoices", {
        name: recForm.name,
        partner_id: inv.partner_id, partner_name: inv.partner_name,
        day_of_month: Number(recForm.day_of_month) || 1,
        due_days: Number(recForm.due_days) || 0,
        discount_global: Number(inv.discount_global || 0),
        items,
        notes: inv.notes || "",
        auto_send: !!recForm.auto_send, sender_user_id: recForm.sender_user_id || "",
        attachments: ["invoice_pdf"], active: recForm.active !== false,
      });
      toast.success("Template berulang dibuat — faktur berikutnya dieksekusi otomatis di menu Faktur Berulang");
      setRecDlg(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat template"); }
    finally { setRecBusy(false); }
  };

  // Prefill dialog dengan data dari dokumen sumber (chain forward)
  const chainForward = (nextDlg, doc, sourceKind = "") => {
    const d = today();
    const cleanItems = (doc.items || []).map(i => ({
      product_id: i.product_id || "", product_name: i.product_name || "",
      item_type: i.item_type || "product", description: i.description || "",
      quantity: Number(i.quantity || 0), unit: i.unit || "pcs",
      price: Number(i.price || 0), cost_price: Number(i.cost_price || 0),
      discount: Number(i.discount || 0), discount_percent: Number(i.discount_percent || 0), tax_rate: Number(i.tax_rate ?? 0.11),
      revenue_account_code: i.revenue_account_code || "4000",
      cogs_account_code: i.cogs_account_code || "5000",
      inventory_account_code: i.inventory_account_code || "1300",
    }));
    const base = {
      date: d, due_date: d,
      partner_id: doc.partner_id || "", partner_name: doc.partner_name || "",
      customer_po_no: doc.customer_po_no || "",
      warehouse_id: doc.warehouse_id || "", project_id: doc.project_id || "",
      project_name: doc.project_name || "",
      items: cleanItems.length ? cleanItems : [emptyItem()],
      notes: doc.notes || "",
      discount_global: doc.discount_global || 0,
      payment_account_id: doc.payment_account_id || "",
    };
    if (nextDlg === "so") setForm({ ...base, quotation_id: doc.id });
    else if (nextDlg === "do") setForm({ ...base, order_id: doc.id });
    else if (nextDlg === "inv") setForm({ ...base, order_id: sourceKind === "so" ? doc.id : (doc.order_id || ""), delivery_id: sourceKind === "delivery" ? doc.id : "",
      dpp_nilai_lain: !!(taxSettings.use_dpp_nilai_lain && partners.find(p => p.id === doc.partner_id)?.is_pkp) });
    else if (nextDlg === "ret") setForm({ ...base, invoice_id: doc.id, reason: "" });
    else if (nextDlg === "rcp") {
      const rem = pph23Remaining(doc, receipts);
      setForm({ invoice_id: doc.id, cash_bank_id: "", date: d, amount: (doc.balance ?? doc.total) || 0, pph23_withheld: rem, notes: "" });
    }
    setOpenDlg(nextDlg);
    toast.info(`Diambil dari ${doc.number}`);
  };

  const submit = async () => {
    try {
      const items = (form.items || []).map(i => ({ ...i, quantity: Number(i.quantity), price: Number(i.price), cost_price: Number(i.cost_price || 0), discount: Number(i.discount || 0), discount_percent: Number(i.discount_percent || 0), tax_rate: (i.tax_rate === "" || i.tax_rate === null || i.tax_rate === undefined) ? 0 : Number(i.tax_rate), pph23_rate: Number(i.pph23_rate || 0) }));
      let path = "";
      if (openDlg === "quot") path = "/sales/quotations";
      else if (openDlg === "so") path = "/sales/orders";
      else if (openDlg === "do") path = "/sales/deliveries";
      else if (openDlg === "inv") path = "/sales/invoices";
      else if (openDlg === "ret") path = "/sales/returns";
      else if (openDlg === "rcp") path = "/sales/receipts";
      const payload = openDlg === "rcp" ? { ...form, amount: Number(form.amount), pph23_withheld: Number(form.pph23_withheld || 0) } : { ...form, items, dp_ids: form.dp_ids || [] };
      if (editingId) {
        await api.put(`${path}/${editingId}`, payload);
        toast.success("Perubahan tersimpan");
      } else {
        await api.post(path, payload);
        toast.success("Tersimpan & jurnal otomatis dibuat");
      }
      setOpenDlg(null); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const pdfInvoice = async (id, num) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/invoices-pdf/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
    } catch { toast.info("PDF export tersedia setelah update backend invoice."); }
  };

  const pdfDelivery = async (id, num) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/documents-pdf/sales-delivery/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
    } catch { toast.error("Gagal unduh Surat Jalan"); }
  };

  const assignEfaktur = async (id, num = "") => {
    if (!window.confirm(`Terbitkan e-Faktur untuk faktur ${num || "ini"}?\n\nSistem akan memesan nomor seri faktur pajak dan menandai faktur sebagai e-Faktur Normal. Lanjutkan?`)) return;
    try {
      const r = await api.post("/efaktur/assign", { invoice_id: id, invoice_opt: "Normal" });
      toast.success(`e-Faktur: ${r.data.tax_invoice_number}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal assign e-Faktur"); }
  };

  const replaceEfaktur = async (id, oldNum) => {
    if (!window.confirm(`Ganti (Replacement) e-Faktur ${oldNum}?\n\nNomor lama akan ditandai "Pengganti" dan sistem memesan NOMOR SERI BARU. Aksi ini tercatat permanen di DJP. Lanjutkan?`)) return;
    const reason = window.prompt(`Ganti e-Faktur ${oldNum}. Alasan penggantian:`, "Koreksi data");
    if (reason === null) return;
    try {
      const r = await api.post("/efaktur/replace", { invoice_id: id, reason });
      toast.success(`Nomor baru: ${r.data.new_number} (Replacement dari ${r.data.old_number})`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ganti e-Faktur"); }
  };

  const cancelEfaktur = async (id, num) => {
    if (!window.confirm(`Batalkan e-Faktur ${num}?\n\nFaktur pajak akan berstatus CANCEL dan tidak dapat dipakai lagi. Aksi ini tidak bisa di-undo. Lanjutkan?`)) return;
    const reason = window.prompt(`Batalkan e-Faktur ${num}. Alasan pembatalan:`, "");
    if (reason === null) return;
    try {
      await api.post("/efaktur/cancel", { invoice_id: id, reason });
      toast.success("e-Faktur dibatalkan (status Cancel)"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal batalkan"); }
  };

  const downloadCoretaxXml = async (id, num) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/efaktur/xml/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || "Gagal"); }
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `coretax_${num}.xml`; a.click();
    } catch (e) { toast.error(e.message || "Gagal download XML"); }
  };

  const downloadCoretaxBulk = async () => {
    try {
      const period = new Date().toISOString().slice(0, 7);
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/efaktur/xml-bulk?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || "Gagal"); }
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `coretax_bulk_${period}.xml`; a.click();
      toast.success(`Bulk XML periode ${period} diunduh`);
    } catch (e) { toast.error(e.message || "Gagal"); }
  };

  const [preserveApproval, setPreserveApproval] = useState(false);
  const BULK_STAGES = {
    quotations: { endpoint: "sales-quotations", filename: "template_penawaran.xlsx" },
    orders: { endpoint: "sales-orders", filename: "template_pesanan.xlsx" },
    deliveries: { endpoint: "sales-deliveries", filename: "template_pengiriman.xlsx" },
    invoices: { endpoint: "sales-invoices", filename: "template_faktur.xlsx" },
    returns: { endpoint: "sales-returns", filename: "template_retur.xlsx" },
    receipts: { endpoint: "sales-receipts", filename: "template_penerimaan.xlsx" },
  };
  const downloadBulkTemplate = async (stageKey = "invoices") => {
    const stage = BULK_STAGES[stageKey] || BULK_STAGES.invoices;
    const token = localStorage.getItem("nusa_token");
    const resp = await fetch(`${API}/bulk-import/${stage.endpoint}/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = stage.filename; a.click();
  };
  const uploadBulk = async (e, stageKey = "invoices") => {
    const file = e.target.files?.[0]; if (!file) return;
    const stage = BULK_STAGES[stageKey] || BULK_STAGES.invoices;
    const extraQs = stageKey === "orders" && preserveApproval ? "&preserve_approval=true" : "";
    const fd = new FormData(); fd.append("file", file);
    const token = localStorage.getItem("nusa_token");
    setImportProg({ loading: true, fileName: file.name });
    try {
      const resp = await fetch(`${API}/bulk-import/${stage.endpoint}?dry_run=true${extraQs}`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const j = await resp.json();
      setImportProg(null);
      if (!resp.ok) throw new Error(j.detail || "Gagal");
      setImportPreview({ stageKey, file, extraQs, ...j });
    } catch (er) { setImportProg(null); toast.error(er.message); }
    finally { e.target.value = ""; }
  };
  const confirmImport = async () => {
    if (!importPreview) return;
    const { stageKey, file, extraQs } = importPreview;
    const stage = BULK_STAGES[stageKey] || BULK_STAGES.invoices;
    const fd = new FormData(); fd.append("file", file);
    const token = localStorage.getItem("nusa_token");
    setImporting(true);
    setImportProg({ loading: true, fileName: file.name });
    try {
      const qs = extraQs ? `?${extraQs.slice(1)}` : "";
      const resp = await fetch(`${API}/bulk-import/${stage.endpoint}${qs}`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const j = await resp.json();
      if (!resp.ok) { setImportProg(null); throw new Error(j.detail || "Gagal"); }
      setImportPreview(null);
      setImportProg({ loading: false, fileName: file.name, result: j });
      load();
    } catch (er) { setImportProg(null); toast.error(er.message); }
    finally { setImporting(false); }
  };

  const sendEmail = async (id) => {
    try {
      const r = await api.post("/send-invoice/email", { invoice_id: id });
      if (r.data.simulated) toast.info(`Simulasi email ke ${r.data.to} (Resend belum di-set)`);
      else toast.success(`Email terkirim ke ${r.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const sendGmail = async (id, provider = "gmail") => {
    try {
      const [prev, tpls] = await Promise.all([
        api.get("/send-invoice/gmail/preview", { params: { invoice_id: id } }),
        api.get("/gmail-templates").catch(() => ({ data: [] })),
      ]);
      const inv = invoices.find(x => x.id === id);
      setGmailInv(inv);
      setGmailDlg(id);
      setGmailProvider(provider);
      setGmailAtts(prev.data.attachments || []);
      setGmailTpls(tpls.data || []);
      setGmailForm({ to: prev.data.to_email || "", cc: prev.data.cc || "", subject: prev.data.subject || "", message: prev.data.message || "", attachments: ["invoice_pdf"] });
      setGmailFiles([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat preview"); }
  };
  const applyTemplate = (tplId) => {
    const tpl = gmailTpls.find(t => t.id === tplId);
    if (!tpl || !gmailInv) return;
    const company = workspace?.settings?.tax?.company_name || workspace?.name || "AIAkuntan";
    const map = {
      "{invoice_number}": gmailInv.number || "",
      "{invoice_date}": gmailInv.date || "",
      "{due_date}": gmailInv.due_date || "",
      "{total}": "Rp " + Number(gmailInv.total || 0).toLocaleString("id-ID"),
      "{partner_name}": gmailInv.partner_name || "",
      "{company_name}": company,
    };
    const render = (s) => Object.entries(map).reduce((a, [k, v]) => a.replaceAll(k, v), s || "");
    setGmailForm({ ...gmailForm, subject: render(tpl.subject), message: render(tpl.body) });
  };
  const addGmailFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...gmailFiles];
    for (const f of picked) {
      if (next.length >= 5) { toast.error("Maksimal 5 lampiran tambahan"); break; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`File '${f.name}' melebihi 10MB`); continue; }
      next.push(f);
    }
    setGmailFiles(next);
    e.target.value = "";
  };
  const submitGmail = async () => {
    setGmailSending(true);
    try {
      const fd = new FormData();
      fd.append("invoice_id", gmailDlg);
      fd.append("to_email", gmailForm.to);
      fd.append("cc", gmailForm.cc);
      fd.append("subject", gmailForm.subject);
      fd.append("message", gmailForm.message);
      gmailForm.attachments.forEach((k) => fd.append("attachments", k));
      gmailFiles.forEach((f) => fd.append("files", f));
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/send-invoice/${gmailProvider}`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.detail || "Gagal");
      toast.success(`Faktur terkirim via ${gmailProvider === "smtp" ? "Email SMTP Anda" : "Gmail Anda"} ke ${j.to}`);
      setGmailDlg(null);
      setGmailFiles([]);
    } catch (e) {
      const msg = e?.message || e?.response?.data?.detail || "Gagal";
      if (String(msg).includes("belum terhubung") || String(msg).includes("belum dikonfigurasi")) toast.error(`${gmailProvider === "smtp" ? "SMTP" : "Gmail"} belum dikonfigurasi. Buka Pengaturan → Integrasi.`);
      else toast.error(msg);
    } finally { setGmailSending(false); }
  };
  const openWa = async (id) => {
    try {
      const r = await api.post("/send-invoice/wa-url", { invoice_id: id });
      window.open(r.data.url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const submitApproval = async (kind, id) => {
    try { await api.post(`/approvals/${kind}/${id}/submit`, { notes: "" }); toast.success("Diajukan untuk approval"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const approveDoc = async (kind, id) => {
    const notes = window.prompt("Catatan approval (opsional):", "") ?? "";
    try { await api.post(`/approvals/${kind}/${id}/approve`, { notes }); toast.success("Dokumen disetujui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const rejectDoc = async (kind, id) => {
    const notes = window.prompt("Alasan penolakan (wajib):", "");
    if (!notes) return toast.error("Alasan wajib diisi");
    try { await api.post(`/approvals/${kind}/${id}/reject`, { notes }); toast.success("Dokumen ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const ApprovalBadge = ({ s }) => {
    if (!s || s === "draft") return <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">Draft</span>;
    if (s === "pending_approval") return <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Menunggu Approval</span>;
    if (s === "approved") return <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Disetujui</span>;
    if (s === "rejected") return <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Ditolak</span>;
    return null;
  };

  const previewDoc = async (stageKey, doc) => {
    // Map row tab key -> backend kind
    const kindMap = { quotations: "sales-quotation", orders: "sales-order", deliveries: "sales-delivery", invoices: "sales-invoice", returns: "sales-return", receipts: "sales-receipt" };
    const kind = kindMap[stageKey]; if (!kind) return;
    try {
      const r = await api.get(`/pdf/${kind}/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPreviewUrl(url);
      setPreviewTitle(`${doc.number} — ${doc.partner_name || ""}`);
      setPreviewMeta({ kind, doc });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal preview"); }
  };

  const downloadPreviewPdf = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl; a.download = `${previewTitle.split(" — ")[0]}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const changePreviewAccount = async (accId) => {
    const d = previewMeta?.doc;
    if (!d || previewMeta.kind !== "sales-invoice") return;
    try {
      await api.put(`/sales/invoices/${d.id}/payment-account`, { payment_account_id: accId });
      toast.success("Rekening pembayaran faktur diperbarui");
      load();
      const r = await api.get(`/pdf/sales-invoice/${d.id}`, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
      setPreviewMeta({ kind: previewMeta.kind, doc: { ...d, payment_account_id: accId } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengganti rekening"); }
  };

  const StatusBadge = ({ s }) => <span className={`text-xs px-2 py-0.5 rounded ${s === "paid" ? "bg-emerald-100 text-emerald-700" : s === "partial" ? "bg-sky-100 text-sky-700" : s === "delivered" || s === "received" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{s}</span>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="sales-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Penjualan</h1>
        <p className="text-sm text-slate-500 mt-1">6 tahap: Penawaran → Pesanan → Pengiriman → Faktur → Retur → Penerimaan. Jurnal otomatis di setiap tahap.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="quotations" data-testid="tab-quotations">Penawaran</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-so">Pesanan</TabsTrigger>
            <TabsTrigger value="deliveries" data-testid="tab-do">Pengiriman</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-inv">Faktur</TabsTrigger>
            <TabsTrigger value="returns" data-testid="tab-ret">Retur</TabsTrigger>
            <TabsTrigger value="receipts" data-testid="tab-rcp">Penerimaan</TabsTrigger>
          </TabsList>
          <div className="relative w-72">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nomor, partner, faktur, catatan…" className="pl-8 h-9" data-testid="sales-search-input" />
          </div>
          <DateRangeFilter range={dateRange} onChange={setDateRange} testidPrefix="sales" />
        </div>

        {[
          { key: "quotations", data: quotations, dlg: "quot", label: "Penawaran" },
          { key: "orders", data: orders, dlg: "so", label: "Sales Order" },
          { key: "deliveries", data: deliveries, dlg: "do", label: "Delivery" },
          { key: "invoices", data: invoices, dlg: "inv", label: "Faktur" },
          { key: "returns", data: returns, dlg: "ret", label: "Retur" },
          { key: "receipts", data: receipts, dlg: "rcp", label: "Penerimaan" },
        ].map(({ key, data, dlg, label }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <div className="flex justify-end mb-3 gap-2">
              {["quotations", "orders", "deliveries", "invoices", "returns", "receipts"].includes(key) && <Button variant="outline" onClick={() => downloadBulkTemplate(key)} data-testid={`bulk-tmpl-${key}-btn`}><DownloadSimple size={14} className="mr-1" />Template Impor</Button>}
              {["quotations", "orders", "deliveries", "invoices", "returns", "receipts"].includes(key) && <Button variant="outline" onClick={async () => { try { const token = localStorage.getItem("nusa_token"); const modMap = { quotations: "sales-quotations", orders: "sales-orders", deliveries: "sales-deliveries", invoices: "sales-invoices", returns: "sales-returns", receipts: "sales-receipts" }; const resp = await fetch(`${API}/export/${modMap[key]}`, { headers: { Authorization: `Bearer ${token}` } }); if (!resp.ok) throw new Error("Gagal export"); const blob = await resp.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${modMap[key]}_${new Date().toISOString().slice(0,10)}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); toast.success("Data diekspor"); } catch (e) { toast.error(e.message); } }} data-testid={`bulk-export-${key}-btn`} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><DownloadSimple size={14} className="mr-1" />Export Excel</Button>}
              {["quotations", "orders", "deliveries", "invoices", "returns", "receipts"].includes(key) && <label className="inline-flex"><input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadBulk(e, key)} data-testid={`bulk-import-${key}-file`} /><span className="inline-flex items-center px-3 h-9 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm gap-1"><UploadSimple size={14} />Impor Excel</span></label>}
              {key === "orders" && <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" title="Jika aktif, dokumen Pesanan yang diperbarui via impor tidak direset ke draft"><input type="checkbox" checked={preserveApproval} onChange={(e) => setPreserveApproval(e.target.checked)} data-testid="preserve-approval-toggle" />Pertahankan approval</label>}
              {key === "invoices" && <Button variant="outline" onClick={downloadCoretaxBulk} data-testid="bulk-xml-btn"><DownloadSimple size={14} className="mr-1" />XML CoreTax</Button>}
              {key === "receipts" && <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setMultiRcpOpen(true)} data-testid="new-multi-rcp-btn"><Plus size={16} className="mr-1" />Penerimaan Multi Faktur</Button>}
              <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => openNew(dlg)} data-testid={`new-${dlg}-btn`}><Plus size={16} className="mr-1" />{label} Baru</Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-md" data-testid={`${key}-table`}>
              <Table>
                <TableHeader><TableRow><TableHead>No</TableHead>{key === "invoices" && <TableHead>e-Faktur</TableHead>}<TableHead>Tanggal</TableHead><TableHead>{key === "receipts" ? "Faktur" : "Partner"}</TableHead>{key !== "receipts" && <TableHead>No. PO</TableHead>}<TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.length === 0 ? <TableRow><TableCell colSpan={(key === "invoices" ? 7 : 6) + (key !== "receipts" ? 1 : 0)} className="text-center py-10 text-slate-500 text-sm">Belum ada data.</TableCell></TableRow> : pgs[key].paged.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono-plex text-xs">{d.number}{d.is_multi && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700" title="Satu penerimaan untuk beberapa faktur" data-testid={`multi-badge-${d.id}`}>Multi</span>}</TableCell>
                      {key === "invoices" && <TableCell className="font-mono-plex text-xs">{d.tax_invoice_number ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{d.tax_invoice_number}</span> : <span className="text-slate-400">—</span>}</TableCell>}
                      <TableCell>{formatDate(d.date)}</TableCell>
                      <TableCell>{key === "receipts" ? <><span className="font-mono-plex text-xs" data-testid={`rcp-inv-${d.id}`}>{d.invoice_number || "—"}</span><div className="text-[11px] text-slate-500">{d.partner_name}</div></> : (d.partner_name || d.invoice_number || "—")}</TableCell>
                      {key !== "receipts" && <TableCell className="font-mono-plex text-xs" data-testid={`po-col-${key}-${d.id}`}>{d.customer_po_no ? <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{d.customer_po_no}</span> : <span className="text-slate-400">—</span>}</TableCell>}
                      <TableCell className="tabular-nums">{formatIDR(d.total || d.amount)}{key === "invoices" && Math.abs((d.balance ?? d.total) - d.total) > 0.01 && d.status !== "paid" && <div className="text-[10px] text-slate-500" data-testid={`inv-balance-${d.id}`}>Sisa: {formatIDR(d.balance)}</div>}</TableCell>
                      <TableCell><StatusBadge s={d.status || "—"} />{key === "orders" && <span className="ml-1"><ApprovalBadge s={d.approval_status} /></span>}{(key === "quotations" || key === "orders") && emailLogs[d.id] && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 inline-flex items-center gap-1" title={`Email terkirim ke ${emailLogs[d.id].to} via ${emailLogs[d.id].channel} · ${(emailLogs[d.id].created_at || "").slice(0, 10)}`} data-testid={`emailsent-${key}-${d.id}`}><EnvelopeSimple size={10} weight="fill" />Terkirim</span>}{d.is_locked && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-700 inline-flex items-center gap-1" title={d.lock_reason}><Lock size={10} weight="fill" />Locked</span>}</TableCell>
                      <TableCell className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => previewDoc(key, d)} data-testid={`preview-${key}-${d.id}`} title="Pratinjau PDF" className="border-sky-300 text-sky-700 hover:bg-sky-50"><Eye size={12} /></Button>
                        {key === "invoices" && (d.balance || 0) <= 0.01 && (d.paid || 0) > 0 && <Button size="sm" variant="outline" onClick={() => { const kindMap = { invoices: "sales-kwitansi" }; const kind = kindMap[key]; api.get(`/pdf/${kind}/${d.id}`, { responseType: "blob" }).then(r => { const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setPreviewUrl(url); setPreviewTitle(`KWITANSI LUNAS — ${d.number}`); }).catch(e => toast.error(e?.response?.data?.detail || "Gagal")); }} data-testid={`kwitansi-${d.id}`} title="Cetak Kwitansi LUNAS" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold"><Receipt size={12} className="mr-1" />Kwitansi LUNAS</Button>}
                        {(key === "quotations" || key === "orders") && !d.is_locked && (key !== "orders" || d.approval_status !== "approved") && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(key === "quotations" ? "quot" : "so", d)} data-testid={`edit-${key}-${d.id}`} title="Edit dokumen"><PencilSimple size={12} /></Button>
                        )}
                        {(key === "quotations" || key === "orders") && (d.is_locked || (key === "orders" && d.approval_status === "approved")) && (
                          <Button size="sm" variant="outline" disabled title={d.lock_reason || "Sudah disetujui — batalkan approval dulu untuk edit"} className="opacity-50 cursor-not-allowed" data-testid={`edit-locked-${d.id}`}><Lock size={12} /></Button>
                        )}
                        {key === "orders" && (d.approval_status === "approved" || d.approval_status === "rejected") && (
                          <Button size="sm" variant="outline" onClick={() => cancelApproval("sales-order", d.id, d.number)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`cancel-appr-${d.id}`} title="Batalkan approval — kembalikan ke draft">Batalkan Approval</Button>
                        )}
                        {(key === "quotations" || key === "orders") && !d.is_locked && (key !== "orders" || d.approval_status !== "approved") && (
                          <Button size="sm" variant="outline" onClick={() => removeDoc(key === "quotations" ? "quot" : "so", d)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`del-${key}-${d.id}`} title="Hapus dokumen"><Trash size={12} /></Button>
                        )}
                        {(key === "deliveries" || key === "invoices") && !d.is_locked && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(key === "deliveries" ? "do" : "inv", d)} data-testid={`edit-${key}-${d.id}`} title="Edit dokumen"><PencilSimple size={12} /></Button>
                        )}
                        {(key === "deliveries" || key === "invoices") && d.is_locked && (
                          <Button size="sm" variant="outline" disabled title={d.lock_reason} className="opacity-50 cursor-not-allowed" data-testid={`edit-locked-${d.id}`}><Lock size={12} /></Button>
                        )}
                        {(key === "deliveries" || key === "invoices" || key === "returns" || key === "receipts") && !d.is_locked && (
                          <Button size="sm" variant="outline" onClick={() => removeDoc(key === "deliveries" ? "do" : (key === "invoices" ? "inv" : (key === "returns" ? "ret" : "rcp")), d)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`del-${key}-${d.id}`} title="Hapus dokumen"><Trash size={12} /></Button>
                        )}
                        {key === "quotations" && !d.is_locked && <Button size="sm" variant="outline" onClick={() => chainForward("so", d)} data-testid={`chain-quot-so-${d.id}`} title="Konversi ke Sales Order" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"><ArrowRight size={12} className="mr-1" />Buat SO</Button>}
                        {key === "orders" && d.approval_status === "approved" && <Button size="sm" variant="outline" onClick={() => chainForward("do", d)} data-testid={`chain-so-do-${d.id}`} title="Konversi ke Pengiriman" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"><ArrowRight size={12} className="mr-1" />Kirim</Button>}
                        {key === "orders" && d.approval_status === "approved" && <Button size="sm" variant="outline" onClick={() => chainForward("inv", d, "so")} data-testid={`chain-so-inv-${d.id}`} title="Konversi ke Faktur" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"><ArrowRight size={12} className="mr-1" />Faktur</Button>}
                        {key === "orders" && <Button size="sm" variant="outline" onClick={() => openDpDlg(d)} data-testid={`dp-btn-${d.id}`} title="Terima Uang Muka (DP)" className="border-amber-300 text-amber-700 hover:bg-amber-50"><Receipt size={12} className="mr-1" />DP</Button>}
                        {key === "deliveries" && <Button size="sm" variant="outline" onClick={() => chainForward("inv", d, "delivery")} data-testid={`chain-do-inv-${d.id}`} title="Konversi ke Faktur" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"><ArrowRight size={12} className="mr-1" />Faktur</Button>}
                        {key === "invoices" && (d.balance || 0) > 0 && <Button size="sm" variant="outline" onClick={() => chainForward("rcp", d)} data-testid={`chain-inv-rcp-${d.id}`} title="Terima Pembayaran" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><ArrowRight size={12} className="mr-1" />Terima</Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => chainForward("ret", d)} data-testid={`chain-inv-ret-${d.id}`} title="Retur Faktur" className="border-rose-300 text-rose-700 hover:bg-rose-50"><ArrowRight size={12} className="mr-1" />Retur</Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => openRecurring(d)} data-testid={`rec-inv-${d.id}`} title="Jadikan template Faktur Berulang — faktur berikutnya terbit otomatis tiap bulan" className="border-amber-300 text-amber-700 hover:bg-amber-50"><Repeat size={12} className="mr-1" />Berulang</Button>}
                        {key === "orders" && (!d.approval_status || d.approval_status === "draft" || d.approval_status === "rejected") && <Button size="sm" variant="outline" onClick={() => submitApproval("sales-order", d.id)} data-testid={`so-submit-${d.id}`}>Ajukan</Button>}
                        {key === "orders" && d.approval_status === "pending_approval" && <><Button size="sm" variant="outline" onClick={() => approveDoc("sales-order", d.id)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`so-approve-${d.id}`}>Setujui</Button><Button size="sm" variant="outline" onClick={() => rejectDoc("sales-order", d.id)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`so-reject-${d.id}`}>Tolak</Button></>}
                        {key === "deliveries" && <Button size="sm" variant="outline" onClick={() => pdfDelivery(d.id, d.number)} data-testid={`pdf-do-${d.id}`} title="Unduh Surat Jalan PDF"><FilePdf size={13} /></Button>}
                        {key === "quotations" && <Button size="sm" variant="outline" onClick={() => setDocEmailDlg({ docType: "sales-quotation", docId: d.id, provider: "gmail" })} data-testid={`gmail-quot-${d.id}`} title="Kirim Penawaran via Gmail Anda" className="border-red-300 text-red-700 hover:bg-red-50"><EnvelopeSimple size={13} weight="fill" /></Button>}
                        {key === "quotations" && <Button size="sm" variant="outline" onClick={() => setDocEmailDlg({ docType: "sales-quotation", docId: d.id, provider: "smtp" })} data-testid={`smtp-quot-${d.id}`} title="Kirim Penawaran via Email SMTP Anda" className="border-orange-300 text-orange-700 hover:bg-orange-50"><EnvelopeSimple size={13} weight="bold" /></Button>}
                        {key === "orders" && <Button size="sm" variant="outline" onClick={() => setDocEmailDlg({ docType: "sales-order", docId: d.id, provider: "gmail" })} data-testid={`gmail-so-${d.id}`} title="Kirim Pesanan Penjualan via Gmail Anda" className="border-red-300 text-red-700 hover:bg-red-50"><EnvelopeSimple size={13} weight="fill" /></Button>}
                        {key === "orders" && <Button size="sm" variant="outline" onClick={() => setDocEmailDlg({ docType: "sales-order", docId: d.id, provider: "smtp" })} data-testid={`smtp-so-${d.id}`} title="Kirim Pesanan Penjualan via Email SMTP Anda" className="border-orange-300 text-orange-700 hover:bg-orange-50"><EnvelopeSimple size={13} weight="bold" /></Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => sendEmail(d.id)} data-testid={`email-${d.id}`} title="Kirim via Email (Resend)"><EnvelopeSimple size={13} /></Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => sendGmail(d.id, "gmail")} data-testid={`gmail-${d.id}`} title="Kirim via Gmail Anda (preview & edit dulu)" className="border-red-300 text-red-700 hover:bg-red-50"><EnvelopeSimple size={13} weight="fill" /></Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => sendGmail(d.id, "smtp")} data-testid={`smtp-${d.id}`} title="Kirim via Email SMTP Anda (preview & edit dulu)" className="border-orange-300 text-orange-700 hover:bg-orange-50"><EnvelopeSimple size={13} weight="bold" /></Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => openWa(d.id)} data-testid={`wa-${d.id}`} title="Kirim via WhatsApp" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><WhatsappLogo size={13} /></Button>}
                        {key === "invoices" && <Button size="sm" variant="outline" onClick={() => pdfInvoice(d.id, d.number)} data-testid={`pdf-inv-${d.id}`}><FilePdf size={13} /></Button>}
                        {key === "invoices" && !d.tax_invoice_number && <Button size="sm" variant="outline" onClick={() => assignEfaktur(d.id, d.number)} data-testid={`efaktur-btn-${d.id}`} title="Terbitkan e-Faktur (akan diminta konfirmasi dulu)"><Receipt size={13} className="mr-1" />e-Faktur</Button>}
                        {key === "invoices" && d.tax_invoice_number && d.tax_invoice_opt !== "Cancel" && <Button size="sm" variant="outline" onClick={() => downloadCoretaxXml(d.id, d.number)} data-testid={`xml-btn-${d.id}`}><DownloadSimple size={13} className="mr-1" />XML</Button>}
                        {key === "invoices" && d.tax_invoice_number && d.tax_invoice_opt !== "Cancel" && <Button size="sm" variant="outline" onClick={() => replaceEfaktur(d.id, d.tax_invoice_number)} data-testid={`replace-btn-${d.id}`} className="border-amber-300 text-amber-700 hover:bg-amber-50">Ganti</Button>}
                        {key === "invoices" && d.tax_invoice_number && d.tax_invoice_opt !== "Cancel" && <Button size="sm" variant="outline" onClick={() => cancelEfaktur(d.id, d.tax_invoice_number)} data-testid={`cancel-efaktur-btn-${d.id}`} className="border-rose-300 text-rose-700 hover:bg-rose-50">Batal</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pgs[key].pager}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <ImportPreviewDialog
        preview={importPreview}
        open={!!importPreview}
        onOpenChange={(o) => { if (!o) setImportPreview(null); }}
        onConfirm={confirmImport}
        importing={importing}
        title={`Pratinjau Impor — ${({ quotations: "Penawaran", orders: "Pesanan", deliveries: "Pengiriman", invoices: "Faktur", returns: "Retur", receipts: "Penerimaan" }[importPreview?.stageKey]) || "Sales"}`}
      />

      {/* Unified dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o) { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); setPreviewMeta(null); } }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 gap-0">
          <DialogHeader className="p-4 border-b border-stone-200 flex flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base">Pratinjau: {previewTitle}</DialogTitle>
            <div className="flex items-center gap-2">
              {previewMeta?.kind === "sales-invoice" && (
                <div className="flex items-center gap-2" data-testid="preview-account-wrap">
                  <Label className="text-xs whitespace-nowrap text-slate-500">Rekening:</Label>
                  <Select value={previewMeta.doc.payment_account_id || "_default"} onValueChange={(v) => changePreviewAccount(v === "_default" ? "" : v)}>
                    <SelectTrigger className="h-8 w-56 text-xs" data-testid="preview-account-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_default">(Default Pengaturan)</SelectItem>
                      {cashBanks.filter(a => a.type === "bank").map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.bank_name ? ` — ${a.bank_name} ${a.account_number || ""}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button size="sm" onClick={downloadPreviewPdf} className="bg-stone-900 hover:bg-stone-800 text-white mr-8" data-testid="preview-download-btn">
                <DownloadSimple size={14} className="mr-1" />Unduh PDF
              </Button>
            </div>
          </DialogHeader>
          {previewUrl && <iframe src={previewUrl} title="preview" className="w-full h-full border-0" data-testid="preview-iframe" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dpDlg} onOpenChange={(o) => { if (!o) setDpDlg(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Uang Muka (DP) — {dpDlg?.order?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            {dpDlg?.order && (
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
                <div><span className="text-slate-500">Pelanggan:</span> <b>{dpDlg.order.partner_name}</b></div>
                <div><span className="text-slate-500">Total SO:</span> <b className="tabular-nums">{formatIDR(dpDlg.order.total)}</b></div>
                {(() => {
                  const existing = dpList.filter(x => x.order_id === dpDlg.order.id);
                  if (existing.length === 0) return null;
                  return (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <div className="text-xs text-slate-500 uppercase mb-1">DP tercatat</div>
                      {existing.map(x => (
                        <div key={x.id} className="text-xs py-1 border-b border-slate-100 last:border-b-0" data-testid={`dp-row-${x.id}`}>
                          <div className="flex justify-between items-center">
                            <span className="font-mono-plex">{x.number}</span>
                            <span className="tabular-nums font-medium">{formatIDR(x.amount)}</span>
                            <span className="text-slate-500 min-w-[80px] text-right">{x.applied_invoice_number ? `→ ${x.applied_invoice_number}` : "belum dipakai"}</span>
                            {!x.applied_invoice_id && <button onClick={() => removeDp(x)} className="text-rose-500 hover:text-rose-700" data-testid={`dp-del-${x.id}`}><Trash size={11} /></button>}
                          </div>
                          {x.with_ppn && (
                            <div className="flex items-center gap-2 mt-1 pl-1">
                              <span className="text-[10px] text-slate-400">DPP {formatIDR(x.dpp)} · PPN {formatIDR(x.ppn)}</span>
                              {x.tax_invoice_number ? (
                                <>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono-plex" data-testid={`dp-efaktur-${x.id}`}>{x.tax_invoice_number}</span>
                                  <button onClick={() => downloadDpXml(x)} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100" data-testid={`dp-xml-${x.id}`}>XML</button>
                                </>
                              ) : (
                                <button onClick={() => assignDpEfaktur(x)} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" data-testid={`dp-assign-efaktur-${x.id}`}>Buat e-Faktur</button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={dpForm.date} onChange={(e) => setDpForm({ ...dpForm, date: e.target.value })} data-testid="dp-date-input" /></div>
              <div><Label>Kas/Bank</Label>
                <Select value={dpForm.cash_bank_id} onValueChange={(v) => setDpForm({ ...dpForm, cash_bank_id: v })}>
                  <SelectTrigger data-testid="dp-cb-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{cashBanks.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Jumlah DP {dpForm.with_ppn && <span className="text-[11px] font-normal text-slate-500 normal-case">(termasuk PPN — gross)</span>}</Label><MoneyInput value={dpForm.amount} onChange={(e) => setDpForm({ ...dpForm, amount: e.target.value })} data-testid="dp-amount-input" /></div>
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded p-2">
              <input type="checkbox" id="dp-with-ppn" checked={dpForm.with_ppn} onChange={(e) => setDpForm({ ...dpForm, with_ppn: e.target.checked })} data-testid="dp-with-ppn-check" />
              <Label htmlFor="dp-with-ppn" className="text-xs font-medium cursor-pointer">DP kena PPN — buat Faktur Pajak DP (bisa di-export ke CoreTax)</Label>
            </div>
            {dpForm.with_ppn && (
              <>
                <div><Label>Tarif PPN</Label>
                  <Select value={String(dpForm.tax_rate)} onValueChange={(v) => setDpForm({ ...dpForm, tax_rate: Number(v) })}>
                    <SelectTrigger data-testid="dp-tax-rate-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.11">11%</SelectItem>
                      <SelectItem value="0.12">12%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {Number(dpForm.amount) > 0 && (
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
                    <div className="flex justify-between"><span className="text-slate-500">DPP (dasar pengenaan)</span><span className="tabular-nums font-medium" data-testid="dp-dpp-preview">{formatIDR(Number(dpForm.amount) / (1 + Number(dpForm.tax_rate)))}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">PPN {Math.round(Number(dpForm.tax_rate) * 100)}%</span><span className="tabular-nums font-medium" data-testid="dp-ppn-preview">{formatIDR(Number(dpForm.amount) - Number(dpForm.amount) / (1 + Number(dpForm.tax_rate)))}</span></div>
                    <div className="flex justify-between pt-1 border-t border-slate-200"><b className="text-slate-700">Total (kas diterima)</b><b className="tabular-nums" data-testid="dp-gross-preview">{formatIDR(Number(dpForm.amount))}</b></div>
                  </div>
                )}
              </>
            )}
            <div><Label>Catatan</Label><Input value={dpForm.notes} onChange={(e) => setDpForm({ ...dpForm, notes: e.target.value })} /></div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3 sm:rounded-b-lg">
              <Button onClick={submitDp} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="dp-submit-btn">Simpan Uang Muka</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!gmailDlg} onOpenChange={(o) => { if (!o) setGmailDlg(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Kirim Faktur via {gmailProvider === "smtp" ? "Email SMTP Anda" : "Gmail Anda"}</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            {gmailTpls.length > 0 && (
              <div>
                <Label>Gunakan Template</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="gmail-tpl-select"><SelectValue placeholder="Pilih template pesan..." /></SelectTrigger>
                  <SelectContent>{gmailTpls.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kepada</Label><Input type="email" value={gmailForm.to} onChange={(e) => setGmailForm({ ...gmailForm, to: e.target.value })} placeholder="email@pelanggan.com" data-testid="gmail-to-input" /><p className="text-[11px] text-slate-500 mt-1" data-testid="gmail-to-multi-hint">Lebih dari satu alamat email? Pisahkan dengan titik koma (;)</p></div>
              <div><Label>CC (opsional)</Label><Input type="email" value={gmailForm.cc} onChange={(e) => setGmailForm({ ...gmailForm, cc: e.target.value })} placeholder="cc@email.com" data-testid="gmail-cc-input" /><p className="text-[11px] text-slate-500 mt-1" data-testid="gmail-cc-multi-hint">Bisa lebih dari satu, pisahkan dengan titik koma (;)</p></div>
            </div>
            <div><Label>Subjek</Label><Input value={gmailForm.subject} onChange={(e) => setGmailForm({ ...gmailForm, subject: e.target.value })} data-testid="gmail-subject-input" /></div>
            <div><Label>Isi Pesan</Label><Textarea rows={7} value={gmailForm.message} onChange={(e) => setGmailForm({ ...gmailForm, message: e.target.value })} data-testid="gmail-message-input" /></div>
            <div>
              <Label>Lampiran</Label>
              <div className="space-y-1.5 mt-1 border border-slate-200 rounded-md p-3" data-testid="gmail-attachments">
                {gmailAtts.map((a) => (
                  <label key={a.key} className={`flex items-center gap-2 text-sm ${a.available ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`} title={a.available ? a.filename : a.note}>
                    <input type="checkbox" disabled={!a.available} checked={gmailForm.attachments.includes(a.key)}
                      onChange={(e) => setGmailForm({ ...gmailForm, attachments: e.target.checked ? [...gmailForm.attachments, a.key] : gmailForm.attachments.filter((x) => x !== a.key) })}
                      data-testid={`gmail-att-${a.key}`} />
                    <span>{a.label}</span>
                    <span className="text-[11px] text-slate-400 font-mono-plex ml-auto">{a.available ? a.filename : a.note}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>File Kontrak / Lampiran Tambahan (opsional, maks 5 × 10MB)</Label>
              <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.zip" onChange={addGmailFiles} data-testid="gmail-file-input" className="block w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:bg-slate-50 file:text-xs hover:file:bg-slate-100" />
              {gmailFiles.length > 0 && (
                <div className="mt-1.5 space-y-1" data-testid="gmail-file-list">
                  {gmailFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                      <span className="flex-1 truncate">{f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span></span>
                      <button type="button" onClick={() => setGmailFiles(gmailFiles.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`gmail-file-remove-${i}`}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={submitGmail} disabled={gmailSending || !gmailForm.to || gmailForm.attachments.length === 0} className={`w-full ${gmailProvider === "smtp" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}`} data-testid="gmail-send-btn">
              {gmailSending ? "Mengirim..." : "Kirim Sekarang"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openDlg} onOpenChange={(o) => { if (!o) { setOpenDlg(null); setEditingId(null); } }}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>
            {editingId && <span className="text-amber-700 font-normal text-sm mr-2">[Edit]</span>}
            {openDlg === "quot" && "Penawaran"}
            {openDlg === "so" && "Pesanan Penjualan"}
            {openDlg === "do" && "Pengiriman"}
            {openDlg === "inv" && "Faktur Penjualan"}
            {openDlg === "ret" && "Retur Penjualan"}
            {openDlg === "rcp" && "Penerimaan Pembayaran"}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            {openDlg === "rcp" ? (
              <>
                <div><Label>Faktur</Label>
                  <Select value={form.invoice_id} onValueChange={(v) => {
                    const inv2 = invoices.find(i => i.id === v);
                    const rem2 = inv2 ? pph23Remaining(inv2, receipts) : 0;
                    const amt2 = inv2?.balance ?? form.amount;
                    setForm({ ...form, invoice_id: v, amount: amt2, pph23_withheld: autoPph23(amt2, inv2, rem2) });
                  }}>
                    <SelectTrigger data-testid="rcp-invoice-select"><SelectValue placeholder="Pilih faktur" /></SelectTrigger>
                    <SelectContent>{invoices.filter(i => (i.balance || 0) > 0).map(i => <SelectItem key={i.id} value={i.id}>{i.number} - {i.partner_name} ({formatIDR(i.balance)})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label>Kas/Bank</Label>
                    <Select value={form.cash_bank_id} onValueChange={(v) => setForm({ ...form, cash_bank_id: v })}>
                      <SelectTrigger data-testid="rcp-cb-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                      <SelectContent>{cashBanks.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Jumlah</Label><MoneyInput value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value, pph23_withheld: autoPph23(e.target.value, rcpInv, pph23Rem) })} data-testid="rcp-amt-input" /></div>
                {pph23Inv > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="rcp-pph23-on-invoice">
                    PPh 23 sebesar <b>{formatIDR(pph23Inv)}</b> sudah dipotong di faktur — sisa tagihan di atas sudah neto, tidak dipotong lagi.
                  </div>
                )}
                {pph23Rem > 0 && (
                  <div>
                    <Label>PPh 23 Dipotong (Rp) <span className="text-slate-400 font-normal normal-case">— sisa {formatIDR(pph23Rem)}</span></Label>
                    <MoneyInput value={form.pph23_withheld || 0} onChange={(e) => setForm({ ...form, pph23_withheld: e.target.value })} data-testid="rcp-pph23-input" />
                  </div>
                )}
                <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between" data-testid="rcp-net-cash-box">
                  <div>
                    <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wide">Kas Diterima di Bank/Kas (otomatis)</div>
                    <div className="text-[10px] text-emerald-600">= Jumlah Faktur − PPh 23 Dipotong</div>
                  </div>
                  <div className="text-xl font-bold tabular-nums text-emerald-900" data-testid="rcp-net-cash">
                    {formatIDR(Math.max(0, Number(form.amount || 0) - Number(form.pph23_withheld || 0)))}
                  </div>
                </div>
              </>
            ) : openDlg === "do" || openDlg === "ret" ? (
              <>
                <div><Label>{openDlg === "do" ? "Pesanan Penjualan" : "Faktur"}</Label>
                  <Select value={form.order_id || form.invoice_id} onValueChange={(v) => {
                    if (openDlg === "do") {
                      const so = orders.find(o => o.id === v);
                      setForm({ ...form, order_id: v, items: so?.items || [emptyItem()], customer_po_no: so?.customer_po_no || form.customer_po_no || "" });
                    } else {
                      const inv = invoices.find(i => i.id === v);
                      setForm({ ...form, invoice_id: v, items: inv?.items || [emptyItem()], customer_po_no: inv?.customer_po_no || form.customer_po_no || "" });
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent>
                      {openDlg === "do" ? orders.filter(o => o.status !== "delivered").map(o => <SelectItem key={o.id} value={o.id}>{o.number} - {o.partner_name}</SelectItem>)
                                        : invoices.map(i => <SelectItem key={i.id} value={i.id}>{i.number} - {i.partner_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>No. PO / Dokumen Pelanggan</Label>
                  <Input value={form.customer_po_no || ""} onChange={(e) => setForm({ ...form, customer_po_no: e.target.value })} placeholder="Terisi otomatis dari dokumen sebelumnya" data-testid="sales-customer-po-do" />
                </div>
                {openDlg === "ret" && <div><Label>Alasan</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Gudang</Label>
                    <Select value={form.warehouse_id || ""} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                      <SelectTrigger data-testid="sales-warehouse-select"><SelectValue placeholder="(default)" /></SelectTrigger>
                      <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} - {w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Proyek</Label>
                    <ProjectCombobox projects={projects} value={form.project_id || ""} testid="sales-project-select"
                      onChange={(p) => setForm({ ...form, project_id: p?.id || "", project_name: p?.name || "" })} />
                  </div>
                </div>
                <ItemsEditor items={form.items || []} setItems={(items) => setForm({ ...form, items })} products={products} />
              </>
            ) : (
              <>
                {openDlg === "so" && (
                  <div><Label>Ambil dari Penawaran (opsional)</Label>
                    <Select value={form.quotation_id || ""} onValueChange={(v) => {
                      const q = quotations.find(x => x.id === v); if (!q) return;
                      const cleanItems = (q.items || []).map(i => ({ ...i, quantity: Number(i.quantity || 0), price: Number(i.price || 0), tax_rate: Number(i.tax_rate ?? 0.11), pph23_rate: Number(i.pph23_rate ?? 0) }));
                      setForm({ ...form, quotation_id: v, partner_id: q.partner_id, partner_name: q.partner_name, items: cleanItems.length ? cleanItems : form.items, warehouse_id: q.warehouse_id || form.warehouse_id, project_id: q.project_id || form.project_id, project_name: q.project_name || form.project_name, notes: q.notes || form.notes, customer_po_no: q.customer_po_no || form.customer_po_no || "" });
                    }}>
                      <SelectTrigger data-testid="so-from-quot-select"><SelectValue placeholder="(Buat langsung tanpa Penawaran)" /></SelectTrigger>
                      <SelectContent>{quotations.filter(q => !form.partner_id || q.partner_id === form.partner_id).map(q => <SelectItem key={q.id} value={q.id}>{q.number} - {q.partner_name} ({formatIDR(q.total)})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {openDlg === "inv" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Dari Sales Order (opsional)</Label>
                      <Select value={form.order_id || ""} onValueChange={(v) => {
                        const o = orders.find(x => x.id === v); if (!o) return;
                        const cleanItems = (o.items || []).map(i => ({ ...i, quantity: Number(i.quantity || 0), price: Number(i.price || 0), tax_rate: Number(i.tax_rate ?? 0.11), pph23_rate: Number(i.pph23_rate ?? 0) }));
                        const availDps = dpList.filter(dp => dp.order_id === v && !dp.applied_invoice_id);
                        const pOfO = partners.find(x => x.id === o.partner_id);
                        setForm({ ...form, order_id: v, partner_id: o.partner_id, partner_name: o.partner_name, items: cleanItems.length ? cleanItems : form.items, warehouse_id: o.warehouse_id || form.warehouse_id, project_id: o.project_id || form.project_id, project_name: o.project_name || form.project_name, dp_ids: availDps.map(d => d.id), tax_regulation_ids: pOfO?.tax_regulation_ids || [], customer_po_no: o.customer_po_no || form.customer_po_no || "" });
                      }}>
                        <SelectTrigger data-testid="inv-from-so-select"><SelectValue placeholder="(Faktur langsung)" /></SelectTrigger>
                        <SelectContent>{orders.filter(o => (o.approval_status === "approved") && (!form.partner_id || o.partner_id === form.partner_id)).map(o => <SelectItem key={o.id} value={o.id}>{o.number} - {o.partner_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Dari Surat Jalan (opsional)</Label>
                      <Select value={form.delivery_id || ""} onValueChange={(v) => {
                        const dl = deliveries.find(x => x.id === v); if (!dl) return;
                        const cleanItems = (dl.items || []).map(i => ({ ...i, quantity: Number(i.quantity || 0), price: Number(i.price || 0), tax_rate: Number(i.tax_rate ?? 0.11), pph23_rate: Number(i.pph23_rate ?? 0) }));
                        const pOfDl = partners.find(x => x.id === dl.partner_id);
                        setForm({ ...form, delivery_id: v, order_id: dl.order_id || form.order_id, partner_id: dl.partner_id, partner_name: dl.partner_name, items: cleanItems.length ? cleanItems : form.items, tax_regulation_ids: pOfDl?.tax_regulation_ids || [], customer_po_no: dl.customer_po_no || orders.find(o => o.id === dl.order_id)?.customer_po_no || form.customer_po_no || "" });
                      }}>
                        <SelectTrigger data-testid="inv-from-do-select"><SelectValue placeholder="(tanpa DO)" /></SelectTrigger>
                        <SelectContent>{deliveries.filter(dl => !form.partner_id || dl.partner_id === form.partner_id).map(dl => <SelectItem key={dl.id} value={dl.id}>{dl.number} - {dl.partner_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Pelanggan</Label>
                    <PartnerCombobox partners={partners} value={form.partner_id} testid="sales-partner-select" onChange={(p) => {
                      const terms = Number(p?.payment_terms_days ?? 30);
                      const base = form.date || today();
                      const due = terms > 0 ? new Date(new Date(base).getTime() + terms * 86400000).toISOString().slice(0,10) : base;
                      setForm({ ...form, partner_id: p.id, partner_name: p?.name || "", tax_regulation_ids: p?.tax_regulation_ids || [],
                                due_date: openDlg === "inv" ? due : form.due_date,
                                dpp_nilai_lain: openDlg === "inv" ? !!(taxSettings.use_dpp_nilai_lain && p?.is_pkp) : (form.dpp_nilai_lain || false) });
                    }} />
                  </div>
                  <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Nomor PO / Dokumen Pelanggan <span className="text-[11px] text-slate-500 font-normal">(opsional — mis. PO-ABC/2026/001)</span></Label>
                    <Input value={form.customer_po_no || ""} onChange={(e) => setForm({ ...form, customer_po_no: e.target.value })} placeholder="Nomor PO/dokumen dari pelanggan" data-testid="sales-customer-po" />
                  </div>
                  {openDlg === "inv" && <>
                    <div><Label>Termin</Label>
                      <Select value={(() => { const d = form.date, dd = form.due_date; if (!d || !dd) return "30"; const diff = Math.round((new Date(dd) - new Date(d)) / 86400000); return [0,7,14,30,45,60].includes(diff) ? String(diff) : "custom"; })()}
                              onValueChange={(v) => {
                                if (v === "custom") return;
                                const n = Number(v); const base = form.date || today();
                                const due = n > 0 ? new Date(new Date(base).getTime() + n * 86400000).toISOString().slice(0,10) : base;
                                setForm({ ...form, due_date: due });
                              }}>
                        <SelectTrigger data-testid="sales-term-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">COD (bayar di tempat)</SelectItem>
                          <SelectItem value="7">7 hari</SelectItem>
                          <SelectItem value="14">14 hari</SelectItem>
                          <SelectItem value="30">30 hari</SelectItem>
                          <SelectItem value="45">45 hari</SelectItem>
                          <SelectItem value="60">60 hari</SelectItem>
                          <SelectItem value="custom">Kustom (atur tanggal)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Jatuh Tempo</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                  </>}
                  <div><Label>Gudang</Label>
                    <Select value={form.warehouse_id || ""} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                      <SelectTrigger data-testid="sales-warehouse-select"><SelectValue placeholder="(default)" /></SelectTrigger>
                      <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} - {w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Proyek</Label>
                    <ProjectCombobox projects={projects} value={form.project_id || ""} testid="sales-project-select"
                      onChange={(p) => setForm({ ...form, project_id: p?.id || "", project_name: p?.name || "" })} />
                  </div>
                </div>
                <ItemsEditor items={form.items || []} setItems={(items) => setForm({ ...form, items })} products={products} />
                {["quot", "so", "inv"].includes(openDlg) && (
                  <div className="flex items-center gap-3 justify-end">
                    <Label className="whitespace-nowrap text-slate-600">Diskon Dokumen (Rp)</Label>
                    <MoneyInput className="w-44" placeholder="0" value={form.discount_global || 0} onChange={(e) => setForm({ ...form, discount_global: e.target.value })} data-testid="sales-discount-global" />
                  </div>
                )}
                {openDlg === "inv" && (
                  <div className="flex items-center gap-3 justify-end">
                    <Label className="whitespace-nowrap text-slate-600">Rekening Pembayaran (PDF)</Label>
                    <Select value={form.payment_account_id || "_default"} onValueChange={(v) => setForm({ ...form, payment_account_id: v === "_default" ? "" : v })}>
                      <SelectTrigger className="w-72" data-testid="sales-payment-account"><SelectValue placeholder="(Default dari Pengaturan)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_default">(Default dari Pengaturan)</SelectItem>
                        {cashBanks.filter(a => a.type === "bank").map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.bank_name ? ` — ${a.bank_name} ${a.account_number || ""}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {openDlg === "inv" && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer bg-slate-50 border border-slate-200 rounded-md p-3">
                    <input type="checkbox" className="mt-0.5" checked={!!form.dpp_nilai_lain} onChange={(e) => setForm({ ...form, dpp_nilai_lain: e.target.checked })} data-testid="inv-dpp-nilai-lain-check" />
                    <span>
                      <span className="font-medium">DPP Nilai Lain (PMK 131/2024)</span>
                      <span className="block text-xs text-slate-500">PPN {Number(taxSettings.default_vat_rate) || 12}% dihitung atas 11/12 × DPP — efektif 11% dari harga jual. Otomatis aktif untuk pelanggan PKP sesuai Pengaturan → Pajak.</span>
                    </span>
                  </label>
                )}
                {openDlg === "inv" && (
                  <TaxRegsPanel
                    taxRegs={taxRegs}
                    value={form.tax_regulation_ids || []}
                    onChange={(ids) => setForm({ ...form, tax_regulation_ids: ids })}
                    subtotal={calcTotals(form.items || []).sub}
                    testidPrefix="inv-taxreg"
                  />
                )}
                {openDlg === "inv" && form.order_id && (() => {
                  const orderDps = dpList.filter(dp => dp.order_id === form.order_id && (!dp.applied_invoice_id || (form.dp_ids || []).includes(dp.id)));
                  if (orderDps.length === 0) return null;
                  const selected = form.dp_ids || [];
                  const totalDp = orderDps.filter(dp => selected.includes(dp.id)).reduce((s, dp) => s + (dp.amount || 0), 0);
                  return (
                    <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="inv-dp-panel">
                      <div className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">Terapkan Uang Muka (DP)</div>
                      <div className="space-y-1">
                        {orderDps.map(dp => (
                          <label key={dp.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={selected.includes(dp.id)} onChange={(e) => setForm({ ...form, dp_ids: e.target.checked ? [...selected, dp.id] : selected.filter(x => x !== dp.id) })} data-testid={`inv-dp-chk-${dp.id}`} />
                            <span className="font-mono-plex text-xs">{dp.number}</span>
                            <span className="text-slate-600">— {formatDate(dp.date)}</span>
                            <span className="tabular-nums ml-auto font-medium">{formatIDR(dp.amount)}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-amber-900 border-t border-amber-200 pt-2 flex justify-between">
                        <span>Total DP dipakai:</span>
                        <span className="font-semibold tabular-nums" data-testid="inv-dp-total">{formatIDR(totalDp)}</span>
                      </div>
                    </div>
                  );
                })()}
                <div><Label>Catatan</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                {openDlg === "inv" && (() => {
                  const t = calcTotals(form.items || [], 0.11, Number(form.discount_global) || 0, !!form.dpp_nilai_lain, (Number(taxSettings.default_vat_rate) || 12) / 100);
                  const regs = (form.tax_regulation_ids || []).map(id => taxRegs.find(r => r.id === id)).filter(Boolean);
                  const dedTotal = regs.reduce((s, r) => s + t.sub * (r.rate || 0) / 100, 0);
                  const dpTotal2 = (form.dp_ids || []).reduce((s, id) => { const dp = dpList.find(d => d.id === id); return s + (dp?.amount || 0); }, 0);
                  return (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1 text-sm" data-testid="inv-totals-summary">
                      <div className="flex justify-between"><span className="text-slate-600">Subtotal (DPP)</span><span className="tabular-nums">{formatIDR(t.sub)}</span></div>
                      {Number(form.discount_global) > 0 && <div className="flex justify-between text-rose-600" data-testid="inv-discount-global-row"><span>Diskon Dokumen (sudah termasuk di DPP)</span><span className="tabular-nums">-{formatIDR(Number(form.discount_global))}</span></div>}
                      {form.dpp_nilai_lain && <div className="flex justify-between text-slate-600" data-testid="inv-dpp-other-row"><span>DPP Nilai Lain (11/12 × DPP)</span><span className="tabular-nums">{formatIDR(t.dppOther)}</span></div>}
                      <div className="flex justify-between"><span className="text-slate-600">PPN</span><span className="tabular-nums">{formatIDR(t.tax)}</span></div>
                      <div className="flex justify-between font-semibold border-t border-slate-200 pt-1"><span>Total Faktur</span><span className="tabular-nums">{formatIDR(t.total)}</span></div>
                      {regs.map(r => (
                        <div key={r.id} className="flex justify-between text-amber-700"><span className="pl-3">− {r.name} ({r.rate}%)</span><span className="tabular-nums" data-testid={`inv-ded-${r.id}`}>-{formatIDR(t.sub * (r.rate || 0) / 100)}</span></div>
                      ))}
                      {dpTotal2 > 0 && <div className="flex justify-between text-amber-700"><span className="pl-3">− Uang Muka (DP)</span><span className="tabular-nums">-{formatIDR(dpTotal2)}</span></div>}
                      <div className="flex justify-between font-bold border-t-2 border-slate-300 pt-1.5"><span>Sisa Tagihan</span><span className="tabular-nums" data-testid="inv-net-total">{formatIDR(Math.max(0, t.total - dedTotal - dpTotal2))}</span></div>
                    </div>
                  );
                })()}
              </>
            )}
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3 sm:rounded-b-lg">
              <Button className="w-full bg-slate-900" onClick={submit} data-testid="sales-submit-btn">Simpan & Post Jurnal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog jadikan faktur berulang */}
      <Dialog open={!!recDlg} onOpenChange={(o) => { if (!o) setRecDlg(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2" data-testid="rec-inv-title"><Repeat size={18} className="text-amber-600" />Jadikan Faktur Berulang</DialogTitle></DialogHeader>
          {recDlg && (
            <div className="space-y-3" data-testid="rec-inv-dialog">
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                Template dibuat dari <b>{recDlg.number}</b> ({recDlg.partner_name}) — {recDlg.items?.length || 0} item, total {formatIDR(recDlg.total)}. Faktur berikutnya diterbitkan otomatis tiap bulan dan bisa dikelola di menu <b>Faktur Berulang</b>.
              </div>
              <div><Label>Nama Template</Label><Input value={recForm.name || ""} onChange={(e) => setRecForm({ ...recForm, name: e.target.value })} data-testid="rec-inv-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Terbit Setiap Tanggal (1-31)</Label><Input type="number" min={1} max={31} value={recForm.day_of_month || 1} onChange={(e) => setRecForm({ ...recForm, day_of_month: e.target.value })} data-testid="rec-inv-dom" /></div>
                <div><Label>Jatuh Tempo (hari)</Label><Input type="number" min={0} value={recForm.due_days ?? 30} onChange={(e) => setRecForm({ ...recForm, due_days: e.target.value })} data-testid="rec-inv-due" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!recForm.auto_send} onChange={(e) => setRecForm({ ...recForm, auto_send: e.target.checked })} data-testid="rec-inv-autosend" />
                Kirim otomatis via email ke pelanggan setelah faktur terbit
              </label>
              {recForm.auto_send && (
                <div><Label className="text-xs">User Pengirim (SMTP/Gmail terhubung)</Label>
                  <Select value={recForm.sender_user_id || ""} onValueChange={(v) => setRecForm({ ...recForm, sender_user_id: v })}>
                    <SelectTrigger data-testid="rec-inv-sender"><SelectValue placeholder={recSenders.filter(s => s.channel).length ? "Pilih user" : "Belum ada user terhubung"} /></SelectTrigger>
                    <SelectContent>{recSenders.filter(s => s.channel).map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email} — {s.channel === "smtp" ? "SMTP" : "Gmail"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <Button className="w-full bg-slate-900" onClick={saveRecurring} disabled={recBusy} data-testid="rec-inv-save">{recBusy ? "Menyimpan…" : "Buat Template Berulang"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <SendDocumentDialog
        open={!!docEmailDlg}
        onClose={() => { setDocEmailDlg(null); load(); }}
        docType={docEmailDlg?.docType}
        docId={docEmailDlg?.docId}
        provider={docEmailDlg?.provider}
      />
      <ImportProgressDialog open={!!importProg} loading={importProg?.loading || false} fileName={importProg?.fileName} result={importProg?.result} onClose={() => setImportProg(null)} />
      <MultiReceiptDialog open={multiRcpOpen} onClose={() => setMultiRcpOpen(false)} partners={partners} cashBanks={cashBanks} onSaved={load} />
    </div>
  );
}
